/* eslint-disable import/prefer-default-export */
import axios from "axios";

import { refreshToken } from "./login";
import logout from "./logout";

import { getMandatoryUserData, getUserCredentials } from "../utils/common";
import { API_URL } from "../utils/Constant";

let isRefreshing = false;
let failedQueue = [];
let refreshPromise = null;
let currentToken = null;

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      // Retry the original request with the new token
      prom.originalRequest.headers.Authorization = `Bearer ${token}`;
      prom.originalRequest._retry = true; // Mark as retried to prevent infinite loops
      prom.resolve(axios(prom.originalRequest));
    }
  });

  failedQueue = [];
};

export const axiosInterceptor = () => {
  axios.interceptors.request.use((request) => {
    const { user_payload, company_name, company_domain } = getMandatoryUserData();
    const userCredentials = getUserCredentials();

    // Skip adding Authorization header for refresh token endpoint (uses refresh_token instead)
    const isRefreshEndpoint = request.url?.includes("/login/refresh/");

    // Use cached token if available, otherwise get from storage
    let tokenToUse = currentToken;

    // If we have cached token but no user credentials, clear cache (user logged out)
    if (tokenToUse && !userCredentials) {
      currentToken = null;
      tokenToUse = null;
    }

    // If no cached token, try to get from storage
    if (!tokenToUse && userCredentials?.firebase_id_token) {
      tokenToUse = userCredentials.firebase_id_token;
      currentToken = tokenToUse; // Cache it for future requests
    }

    // Add Authorization header with Firebase token
    if (tokenToUse && !isRefreshEndpoint) {
      request.headers.Authorization = `Bearer ${tokenToUse}`;
    }

    if (user_payload && company_name) {
      const urlArr = request.url.split("?");
      if (urlArr.length > 1 && urlArr[0].length > 0) {
        const queryParams = new URLSearchParams(urlArr[1]);
        queryParams.delete("user_payload");
        queryParams.delete("company_name");
        queryParams.delete("company_domain");
        const newQueryParamsString = queryParams.toString();
        request.url = `${urlArr[0]}?${newQueryParamsString}`;
      }
      /* Replace fields in params or data obj based on API method */
      const key = request.method === "get" ? "params" : "data";
      if (!request[key]) {
        request[key] = {};
      }

      if (request[key].disableInterceptor) { // don't add to payload if disableInterceptor is true
        delete request[key].disableInterceptor;
      } else {
        request[key].user_payload = user_payload;
        request[key].company_name = company_name;
        request[key].company_domain = company_domain;
      }
    }
    return request;
  }, () => {
    /** Navigate to maintenance page if request error */
    window.location.pathname = "/maintenance";
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const statusCode = error.response?.status;
      const errorDetail = error.response?.data?.detail;
      const errorMessage = error.response?.data?.message;
      const isTokenExpired = errorDetail === "Token has expired." || errorMessage === "Token has expired.";
      const loggedIn = !!getMandatoryUserData().user_payload;
      const originalRequest = error.config;

      /** Handle 401 Unauthorized or Token expired message - Token expired or invalid */
      if ((statusCode === 401 || isTokenExpired) && loggedIn && !originalRequest._retry) {
        // Skip refresh for refresh endpoint itself to prevent infinite loops
        const isRefreshEndpoint = originalRequest.url?.includes("/login/refresh/");
        if (isRefreshEndpoint) {
          currentToken = null; // Clear cached token
          sessionStorage.clear();
          window.location.pathname = "/login";
          return Promise.reject(error);
        }

        if (isRefreshing && refreshPromise) {
          // If already refreshing, queue this request and wait for the existing refresh
          return new Promise((resolve, reject) => {
            failedQueue.push({
              resolve,
              reject,
              originalRequest,
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        // Create a promise for the refresh that can be shared by queued requests
        refreshPromise = (async () => {
          try {
            const result = await refreshToken();

            if (result.success && result.firebase_id_token) {
              currentToken = result.firebase_id_token;

              // Process queued requests with the new token
              processQueue(null, result.firebase_id_token);

              // Retry original request with new token
              originalRequest.headers.Authorization = `Bearer ${result.firebase_id_token}`;
              isRefreshing = false;
              refreshPromise = null;
              return result.firebase_id_token;
            }
            // Refresh failed, clear storage and redirect to login
            currentToken = null; // Clear cached token
            processQueue(new Error(result.message || "Token refresh failed"), null);
            isRefreshing = false;
            refreshPromise = null;
            sessionStorage.clear();
            window.location.pathname = "/login";
            throw new Error(result.message || "Token refresh failed");
          } catch (refreshError) {
            currentToken = null; // Clear cached token
            processQueue(refreshError, null);
            isRefreshing = false;
            refreshPromise = null;
            sessionStorage.clear();
            window.location.pathname = "/login";
            throw refreshError;
          }
        })();

        return refreshPromise
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axios(originalRequest);
          })
          .catch((refreshError) => Promise.reject(refreshError));
      }

      /** Handle 403 Forbidden */
      if (loggedIn && statusCode === 403) {
        const isNoAccessError = errorMessage === "User has no access";

        // If message is "User has no access", automatically sign out user
        if (isNoAccessError) {
          logout().catch(() => {
            // Silently handle errors - will still clear storage
          });
          currentToken = null;
          sessionStorage.clear();
          window.location.pathname = "/login";
          return Promise.reject(error);
        }

        // If message is NOT "User has no access" and URL matches API_URL, navigate to auth-failed page
        if (error.config.url === API_URL) {
          window.location.pathname = "/auth-failed";
          return Promise.reject(error);
        }
      }

      /** Navigate to maintenance page if status 500 */
      if (statusCode === 500) {
        window.location.pathname = "/maintenance";
      }

      return Promise.reject(error);
    },
  );
};
